/**
 * Tracked links (Starter `trackedLinks`), buyer side.
 *
 * A creator's short link (`<store>/go/<slug>`) redirects to their store with
 * `?pf_link=<id>`. The store keeps that id for 30 days in a first-party
 * cookie on its own host (plus localStorage, for browsers that cap script
 * cookies), and every checkout sends it as `trackedLink` so the sale is
 * credited to the link. A newer `pf_link` always replaces the kept one:
 * last click wins. The backend only credits a link of the seller being
 * bought from, so a stale id from another store is harmless.
 *
 * Safe without a window (SSR / tests) and when storage is blocked.
 */

export const TRACKED_LINK_PARAM = "pf_link";
export const TRACKED_LINK_COOKIE = "pf_link";
export const TRACKED_LINK_STORAGE_KEY = "pf-tracked-link";
export const TRACKED_LINK_WINDOW_DAYS = 30;
const WINDOW_MS = TRACKED_LINK_WINDOW_DAYS * 24 * 60 * 60 * 1000;

const ID_RE = /^[a-f0-9]{24}$/;

const cleanId = (value: unknown): string | null => {
	if (typeof value !== "string") return null;
	const id = value.trim().toLowerCase();
	return ID_RE.test(id) ? id : null;
};

const fromSearch = (search: string): string | null => {
	try {
		return cleanId(new URLSearchParams(search).get(TRACKED_LINK_PARAM));
	} catch {
		return null;
	}
};

const readCookie = (doc: Document): string | null => {
	try {
		for (const part of doc.cookie.split(";")) {
			const [name, ...rest] = part.trim().split("=");
			if (name === TRACKED_LINK_COOKIE) return cleanId(rest.join("="));
		}
	} catch {
		/* cookies blocked */
	}
	return null;
};

const readStored = (win: Window, now: number): string | null => {
	try {
		const raw = win.localStorage?.getItem(TRACKED_LINK_STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as { id?: unknown; at?: unknown };
		const at = Number(parsed?.at);
		if (!Number.isFinite(at) || now - at > WINDOW_MS || at > now + 60_000) {
			win.localStorage.removeItem(TRACKED_LINK_STORAGE_KEY);
			return null;
		}
		return cleanId(parsed?.id);
	} catch {
		return null;
	}
};

/**
 * Keeps the `pf_link` of the current URL (if any) for 30 days, replacing
 * any older one. Returns the kept id. Call once per page load.
 */
export function captureTrackedLink(
	win: Window | undefined = typeof window === "undefined" ? undefined : window,
	now: number = Date.now(),
): string | null {
	if (!win) return null;
	const id = fromSearch(win.location?.search || "");
	if (!id) return null;
	try {
		const secure = win.location.protocol === "https:" ? "; Secure" : "";
		win.document.cookie = `${TRACKED_LINK_COOKIE}=${id}; Max-Age=${
			TRACKED_LINK_WINDOW_DAYS * 24 * 60 * 60
		}; Path=/; SameSite=Lax${secure}`;
	} catch {
		/* cookies blocked */
	}
	try {
		win.localStorage?.setItem(
			TRACKED_LINK_STORAGE_KEY,
			JSON.stringify({ id, at: now }),
		);
	} catch {
		/* storage blocked */
	}
	return id;
}

/**
 * The tracked link to credit a checkout to: the current URL's `pf_link`,
 * else the kept cookie, else the kept localStorage entry (within 30 days).
 * Null when the buyer came through no link.
 */
export function readTrackedLink(
	win: Window | undefined = typeof window === "undefined" ? undefined : window,
	now: number = Date.now(),
): string | null {
	if (!win) return null;
	return (
		fromSearch(win.location?.search || "") ??
		readCookie(win.document) ??
		readStored(win, now)
	);
}

/** `{ trackedLink }` to spread into a checkout request body, or `{}`. */
export const trackedLinkBody = (): { trackedLink?: string } => {
	const id = readTrackedLink();
	return id ? { trackedLink: id } : {};
};

// Pure (no window/document): the Cloudflare Worker bundles it too.
export { trackedLinkApiUrl } from "./trackedLinkUrl";
